import { Box, SimpleGrid, Text, Group, Tooltip } from '@mantine/core'
import type { CategorySummary } from '@/api/security'
import { cellState, cellBackground, NA_BORDER, NA_TEXT } from './heatmapColors'

const CATEGORY_NAMES: Record<string, string> = {
  A01: 'Broken Access Control',
  A02: 'Cryptographic Failures',
  A03: 'Injection',
  A04: 'Insecure Design',
  A05: 'Security Misconfiguration',
  A06: 'Vulnerable Components',
  A07: 'Auth Failures',
  A08: 'Data Integrity Failures',
  A09: 'Logging Failures',
  A10: 'SSRF',
}

/** Cadeado SVG inline (sem emoji) — marca a célula "não avaliado". */
function LockIcon({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth={2} strokeLinecap="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="1" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  )
}

interface OwaspHeatmapProps {
  categories: CategorySummary[]
  onSelect?: (categoryId: string) => void
}

export function OwaspHeatmap({ categories, onSelect }: OwaspHeatmapProps) {
  return (
    <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
      {categories.map((cat) => {
        const state = cellState(cat)
        const na = state === 'na'
        const bg = cellBackground(state)
        const fg = na ? NA_TEXT : '#ffffff'
        const name = CATEGORY_NAMES[cat.id] ?? cat.id
        return (
          <Tooltip
            key={cat.id}
            label={na ? `${name} — não avaliado neste scan` : name}
            withArrow
          >
            <Box
              data-testid={`heat-cell-${cat.id}`}
              data-state={state}
              role="button"
              tabIndex={0}
              onClick={() => onSelect?.(cat.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSelect?.(cat.id) }}
              style={{
                background: bg,
                border: na ? `1.5px dashed ${NA_BORDER}` : 'none',
                borderRadius: 'var(--mantine-radius-md)',
                padding: '14px 14px',
                cursor: 'pointer',
                color: fg,
                textAlign: 'left',
                transition: 'filter var(--ai-transition-base), transform var(--ai-transition-base)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)' }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)' }}
            >
              <Group justify="space-between" align="flex-start" gap={6} wrap="nowrap">
                <Box>
                  <Text fw={800} size="lg" ff="var(--mantine-font-family-monospace)">{cat.id}</Text>
                  <Text size="xs" lineClamp={2} style={{ opacity: na ? 1 : 0.9 }}>{name}</Text>
                </Box>
                {na && <LockIcon color={NA_TEXT} />}
              </Group>
              {na ? (
                <Text size="xs" mt={8} c={NA_TEXT}>não avaliado</Text>
              ) : (
                <Text fw={800} size="1.6rem" mt={6}
                      ff="var(--mantine-font-family-monospace)">
                  {cat.open_count}
                </Text>
              )}
            </Box>
          </Tooltip>
        )
      })}
    </SimpleGrid>
  )
}
